/**
 * useChatHistory Hook
 * Manages individual chat history and messages
 */

import { useState, useCallback } from "react";
import {
  ChatService,
  Message,
  ChatHistoryResponse,
} from "../services/api/chat";

export function useChatHistory() {
  const [history, setHistory] = useState<ChatHistoryResponse | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Parse message content - handles JSON array format from API
   * User messages may be stored as [{"type":"text","text":"..."}]
   */
  const parseMessageContent = (content: string, role: string): string => {
    if (role === "user") {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
          return parsed
            .filter((item: any) => item.type === "text" && item.text)
            .map((item: any) => item.text)
            .join(" ");
        }
      } catch {
        // Not JSON, return as-is
      }
    }
    return content;
  };

  /**
   * Load chat history
   */
  const loadHistory = useCallback(async (chatId: string) => {
    try {
      setLoading(true);
      setError(null);
      const historyData = await ChatService.getChatHistory(chatId);
      setHistory(historyData);

      // Parse message content to handle JSON format
      const parsedMessages = (historyData.messages || []).map((msg) => ({
        ...msg,
        content: parseMessageContent(msg.content, msg.role),
      }));
      setMessages(parsedMessages);
    } catch (err: any) {
      console.error("Failed to load chat history:", err);
      setError(err.message || "Failed to load chat history");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add a new message to the current chat
   */
  const addMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  /**
   * Clear current chat history
   */
  const clearHistory = useCallback(() => {
    setHistory(null);
    setMessages([]);
    setError(null);
  }, []);

  /**
   * Review a message (like/dislike)
   */
  const reviewMessage = useCallback(
    async (messageId: string, review: "like" | "dislike") => {
      if (!history?.chat_id) {
        throw new Error("No active chat");
      }

      try {
        await ChatService.reviewMessage(history.chat_id, messageId, review);
      } catch (err: any) {
        console.error("Failed to review message:", err);
        throw err;
      }
    },
    [history],
  );

  return {
    history,
    messages,
    loading,
    error,
    loadHistory,
    addMessage,
    clearHistory,
    reviewMessage,
  };
}
